import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import useCompete from '@stores/compete';
import { emptyList } from '@constants/empty';
import { useIsMobile } from '@stores/AppCommonData';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { segment } from '@actions/analytics';
import { RATINGS_SECTION_VIEWED } from '@cont/Compete/constants';
import { useRouter } from 'next/router';
import Link from 'aquilla/core/molecules/Link';
import Image from '@base/FutureImage';
import RatingPill from './RatingPill';
import RatingProgressModal from '../RatingProgressModal';
import useRatingProgressInfo from '../useRatingProgressInfo';

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  ${mediaQuery.uptoMobile} {
    height: 208px;
  }
  position: relative;
  .rating-pill {
    margin: ${Spacings.SPACING_3B} 0;
  }
  .rating-pill:nth-of-type(1) {
    margin-top: ${Spacings.SPACING_4B};
  }
  .rating-pill:nth-of-type(4) {
    margin-bottom: 0;
  }
`;

const NoRatingWrapper = styled.div`
  height: 100%;
  display: flex;
  user-select: none;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`;

const StyledH5 = styled(Typography)`
  margin-top: ${Spacings.SPACING_4B};
`;

const StyledP3 = styled(Typography)`
  margin-top: ${Spacings.SPACING_2};
  color: var(--color-text-secondary);
`;

const StyledSubtext = styled(StyledP3)`
  margin-top: 0;
  margin-left: ${Spacings.SPACING_3B};
  max-width: 170px;
`;

const IconWrapper = styled.div`
  width: ${Spacings.SPACING_8B};
  height: ${Spacings.SPACING_8B};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CompeteMoreWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${Spacings.SPACING_16B};
  width: 100%;
  position: absolute;
  bottom: 0;
`;

const StyledLink = styled(Link)`
  display: flex;
  border-top: 1px solid var(--color-divider);
  margin-top: ${Spacings.SPACING_4B};
  padding-top: ${Spacings.SPACING_4B};
  height: ${Spacings.SPACING_9B};
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  width: 100%;
  ${mediaQuery.uptoMobile} {
    height: ${Spacings.SPACING_11B};
    border: 1px solid var(--color-divider);
    border-radius: ${Spacings.SPACING_2B};
    padding: 0;
  }
`;

const RANK_LIST_MAX_LEN = 4;

const RatingActivity = () => {
  const isMobile = useIsMobile();
  const { data } = useRatingProgressInfo();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { setSelectedDuration } = useCompete();
  const { goalUID } = useRouter();
  const goalSegmentData = useGoalEventProps({ goalUID });

  useEffect(() => {
    if (!isModalOpen && data.durationFilters?.items) {
      const defaultFilter = data.durationFilters.items.find(
        (item) => item.defaultFilter === true
      );
      if (defaultFilter?.value) {
        setSelectedDuration(defaultFilter.value);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.durationFilters?.items, isModalOpen]);

  const handleOnCTAClick = () => {
    setIsModalOpen(true);
    segment.track(RATINGS_SECTION_VIEWED, { ...goalSegmentData });
  };

  const renderRatingInfo = () => {
    const { userActivity = emptyList } = data;

    if (userActivity?.length) {
      return (
        <>
          <Typography variant={isMobile ? 'h6' : 'h5'}>
            Rating activity
          </Typography>
          {userActivity.slice(0, RANK_LIST_MAX_LEN).map((rankInfo) => {
            return (
              <RatingPill key={rankInfo.opponentUserUid} rankInfo={rankInfo} />
            );
          })}
          {userActivity?.length < RANK_LIST_MAX_LEN ? (
            <CompeteMoreWrapper>
              <IconWrapper>
                <Image
                  src="compete/compete-graph-up.svg"
                  width={32}
                  height={32}
                  alt="graph up"
                />
              </IconWrapper>
              <StyledSubtext>Compete more to improve your rating</StyledSubtext>
            </CompeteMoreWrapper>
          ) : (
            <StyledLink
              rightArrow
              underline={false}
              onClick={(e) => {
                e.preventDefault();
                handleOnCTAClick();
              }}
              href="/"
            >
              <Typography variant="h6" color="var(--color-text-primary)">
                View activity
              </Typography>
            </StyledLink>
          )}
        </>
      );
    }

    return (
      <NoRatingWrapper>
        <Image
          src="compete/no-rating-activity.svg"
          width={80}
          height={80}
          alt="no rating"
        />
        <StyledH5 variant="h5">You haven&apos;t competed yet</StyledH5>
        <StyledP3 variant="p3">
          Your Compete matches history will be shown here
        </StyledP3>
      </NoRatingWrapper>
    );
  };

  const handleModalClose = () => setIsModalOpen(false);

  return (
    <>
      <Wrapper>{renderRatingInfo()}</Wrapper>
      <RatingProgressModal
        isModalOpen={isModalOpen}
        onCloseModal={handleModalClose}
      />
    </>
  );
};

export default RatingActivity;
